import { loadGoogleMaps } from './googleMaps';
import { roundCoordinate, type LatLng } from './mapProvider';

/**
 * Searching for a place by name, so a customer need not drag a map across a
 * city to find their own street.
 *
 * WHY WE RENDER OUR OWN LIST rather than using Google's drop-in autocomplete
 * widget. The widget brings its own markup, focus behaviour and styling, none
 * of which match the rest of this checkout, and it cannot be made to. Asking
 * for the suggestions as data and drawing them ourselves costs one more
 * function and keeps the search box looking and behaving like every other
 * field on the page.
 *
 * SESSION TOKENS ARE A BILLING DETAIL, and the reason this is not a bare
 * fetch per keystroke. Google bills autocomplete per SESSION when a token
 * groups the keystrokes with the final place lookup, and per REQUEST when it
 * does not. Without the token a customer typing "Kalamassery" slowly is
 * eleven billable requests instead of one.
 */

export interface PlaceSuggestion {
  /** Opaque; pass back to `resolvePlace`. */
  id: string;
  /** "Sahya Building" — what the customer is looking for. */
  primary: string;
  /** "University Road, Kalamassery, Kochi" — which one of them it is. */
  secondary: string;
}

/** Google's own type, kept out of the component's sight. */
type SessionToken = google.maps.places.AutocompleteSessionToken;

export interface PlaceSearchSession {
  suggest: (input: string) => Promise<PlaceSuggestion[]>;
  resolve: (id: string) => Promise<LatLng | null>;
}

/**
 * One search session: every keystroke plus the place finally chosen.
 *
 * A session ENDS when a place is resolved, so a new one is minted after each
 * selection. Reusing a token across selections is what Google's billing docs
 * describe as "not a session", and it is charged accordingly.
 */
export async function createPlaceSearch(bias?: LatLng): Promise<PlaceSearchSession> {
  const google = await loadGoogleMaps();
  const places = google.maps.places;

  let token: SessionToken = new places.AutocompleteSessionToken();

  return {
    async suggest(input: string): Promise<PlaceSuggestion[]> {
      const trimmed = input.trim();
      if (trimmed.length < 3) return [];

      const request: google.maps.places.AutocompleteRequest = {
        input: trimmed,
        sessionToken: token,
        // India only. The shop delivers in one city; offering a customer a
        // street in another country is noise they have to read past.
        includedRegionCodes: ['in'],
      };
      // Bias, not restrict: the shop's own area floats to the top, but a
      // customer whose address genuinely sits outside it can still find it —
      // and the carrier, not this box, decides what is deliverable.
      if (bias) {
        request.locationBias = { center: bias, radius: 30_000 };
      }

      const { suggestions } =
        await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

      const found: PlaceSuggestion[] = [];
      for (const suggestion of suggestions) {
        const prediction = suggestion.placePrediction;
        if (!prediction) continue;
        found.push({
          id: prediction.placeId,
          primary: prediction.mainText?.toString() ?? prediction.text.toString(),
          secondary: prediction.secondaryText?.toString() ?? '',
        });
      }
      return found;
    },

    async resolve(id: string): Promise<LatLng | null> {
      const place = new places.Place({ id });
      await place.fetchFields({ fields: ['location'] });
      // The session is spent. The next search starts a new one, which is what
      // keeps the per-session billing honest.
      token = new places.AutocompleteSessionToken();

      const location = place.location;
      if (!location) return null;
      return {
        lat: roundCoordinate(location.lat()),
        lng: roundCoordinate(location.lng()),
      };
    },
  };
}
